import moment from 'moment';
import 'moment-timezone';
import {
  IAddress,
  IAppointment,
  ICart,
  ICartLineItem,
  IConsultation,
  ICoupon,
  ILicense,
  ICustomerUser,
  IEMRLayout,
  IOrder,
  IProductSubscriptionPhase,
  OrderPrescriptionType,
  Prescription,
  SubscriptionCycleUnit,
  UserRole
} from '@care-portals/api-interfaces';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  isAfter,
  isThisYear,
  parseISO,
  startOfDay,
  subDays,
  subHours,
  subMinutes
} from 'date-fns';
import { camelCase } from 'lodash';
import { QuestionsCatalog, caProvinces, formTypes, usStates } from '@care-portals/data';

/** @deprecated 'user localCompare instead' */
export const stringCompare = (a: string, b: string) => {
  if (!a) {
    return 1;
  } else if (!b) {
    return -1;
  }

  if (a.toLowerCase() === b.toLowerCase()) {
    return 0;
  }

  return a.toLowerCase() < b.toLowerCase() ? 1 : -1;
};

export const valueCompare = (a: Date | number, b: Date | number) => {
  if (a === undefined && b === undefined) {
    return 0;
  } else if (a === undefined) {
    return -1;
  } else if (b === undefined) {
    return 1;
  }

  return a > b ? 1 : -1;
};

export const dateCompare = (a: Date | number, b: Date | number) => {
  if (!a && !b) {
    return 0;
  } else if (!a) {
    return -1;
  } else if (!b) {
    return 1;
  }

  return new Date(a) > new Date(b) ? 1 : -1;
};

/**
 * Returns true when `lastAt` falls within the cooldown window described by
 * `duration` (e.g. "30m", "24h", "7d"). Supported units: m=minutes, h=hours,
 * d=days. Invalid/missing inputs => false (do not skip).
 */
export const isWithinContactCooldown = (
  lastAt: Date | string | null | undefined,
  duration: string | null | undefined
): boolean => {
  if (!lastAt || !duration) return false;

  const match = String(duration)
    .trim()
    .match(/^(\d+)([mhd])$/i);
  if (!match) return false;

  const amount = parseInt(match[1], 10);
  if (!amount || amount < 1) return false;

  const lastDate = new Date(lastAt);
  if (Number.isNaN(lastDate.getTime())) return false;

  const unit = match[2].toLowerCase();
  const now = new Date();
  let threshold: Date;
  if (unit === 'm') {
    threshold = subMinutes(now, amount);
  } else if (unit === 'h') {
    threshold = subHours(now, amount);
  } else {
    threshold = subDays(now, amount);
  }

  return lastDate.getTime() >= threshold.getTime();
};

export const timeDifference = (date1: Date, date2: Date) => {
  const sign = date1 > date2 ? '' : '-';
  const diff = Math.abs(date1.getTime() - date2.getTime());
  const diffDays = Math.floor(diff / 86400000); // days
  const diffHrs = Math.floor((diff % 86400000) / 3600000); // hours
  const diffMins = Math.round(((diff % 86400000) % 3600000) / 60000); // minutes

  return (
    sign +
    (diffDays ? diffDays + 'days ' : '') +
    (diffHrs ? diffHrs + 'hrs ' : '') +
    (diffMins + 'mins')
  );
};

export const getStartOfDay = (date: Date) => {
  // @ts-ignore
  return moment(date).tz('Canada/Eastern').startOf('day').toDate();
};

export const getEndOfDay = (date: Date) => {
  // @ts-ignore
  return moment(date).tz('Canada/Eastern').endOf('day').toDate();
};

export const getDayWithTimezone = (date: Date) => {
  // @ts-ignore
  return moment(date).tz('Canada/Eastern').format('yyyy-MM-DD');
};

export const getDateString = (d: Date) => {
  return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
};

const CALENDAR_DATE_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Lab result collectedAt helpers (date-only, no timezone day shift).
 * For Canada/Eastern "today" checks, use getDayWithTimezone.
 */

/** `YYYY-MM-DD` from the user's local calendar (e.g. default for a new date input). */
export function formatLocalCalendarDateForInput(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** `YYYY-MM-DD` for `<input type="date">` from a stored calendar date (UTC-safe). */
export function formatCalendarDateForInput(
  value: Date | string | null | undefined
): string {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'string' && CALENDAR_DATE_INPUT_RE.test(value.trim())) {
    return value.trim();
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Parse `YYYY-MM-DD` as a calendar date at UTC noon (avoids timezone day shifts). */
export function parseCalendarDateFromInput(
  value: string | null | undefined
): Date | undefined {
  if (value == null) {
    return undefined;
  }
  const match = CALENDAR_DATE_INPUT_RE.exec(value.trim());
  if (!match) {
    return undefined;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
}

/** Normalize lab result collectedAt for persistence (UTC noon for date-only strings). */
export function normalizeCollectedAtValue(
  value: Date | string | null | undefined
): Date | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  const fromInput = parseCalendarDateFromInput(value);
  if (fromInput) {
    return fromInput;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** Human-readable collected date (no time-of-day) for lab results. */
export function formatCalendarDateDisplay(
  value: Date | string | null | undefined,
  locale?: string
): string {
  if (value == null || value === '') {
    return '-';
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '-';
  }
  return d.toLocaleDateString(locale, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export const formattedDate = (
  year: number | string,
  month: number | string,
  day: number | string
) => {
  if (typeof year === 'string') {
    year = parseInt(year, 10);
  }
  if (typeof month === 'string') {
    month = parseInt(month, 10);
  }
  if (typeof day === 'string') {
    day = parseInt(day, 10);
  }

  if (year < 1900 || year > 2100) {
    return '';
  }
  if (month < 1 || month > 12) {
    return '';
  }
  return `${year}-${('0' + month).slice(-2)}-${('0' + day).slice(-2)}`;
};

export const isToday = (dayString: string) => {
  return dayString === getDayWithTimezone(new Date());
};

export const getAuthCookieDomain = (hostname: string) => {
  return hostname
    .split('.')
    .splice(-2) // remove subdomains if exists
    .join('.')
    .split(':')[0] // remove port if exist
    .toLowerCase();
};

export const getMaxValue = (values: string[]) => {
  if (!values || !values.length) {
    return null;
  }
  return values.reduce((max, id) => (id > max ? id : max), values[0]);
};

export const getMinValue = (values: string[]) => {
  if (!values || !values.length) {
    return null;
  }
  return values.reduce((min, id) => (id < min ? id : min), values[0]);
};

/** @deprecated this is only used with bigcommerce, use coupons manager instead */
export const calcDiscountAmount = (cart: ICart, coupon: ICoupon): number => {
  if (!coupon) {
    return 0;
  }

  if (coupon.affectedProducts?.length > 0) {
    // apply coupon on product level
    const cartItems = [
      ...cart.line_items.physical_items,
      ...cart.line_items.digital_items
    ];

    let discount = 0;
    const discountedMap = {};
    const isItemIncludedInCouponDiscount = (item: ICartLineItem) =>
      coupon.affectedProducts.includes(item.sku); // we save the stripe price in item sku
    const alreadyDiscounted = (item: ICartLineItem) =>
      !!discountedMap[item.sku];
    for (const item of cartItems) {
      if (!isItemIncludedInCouponDiscount(item) || alreadyDiscounted(item)) {
        continue;
      }

      if (coupon.discountType === 'percent') {
        const itemDiscountAmount =
          (coupon.discount / 100) * item.list_price * item.quantity;
        discountedMap[item.sku] = itemDiscountAmount;
        discount += itemDiscountAmount;
      } else if (coupon.discountType === 'dollar') {
        discount = coupon.discount;
        break; // fixed coupon discount applies only on one product
      }
    }
    return discount;
  } else {
    // apply coupon on cart level
    if (coupon.discountType === 'percent') {
      return cart.base_amount * (coupon.discount / 100);
    } else if (coupon.discountType === 'dollar') {
      return coupon.discount;
    }

    return 0;
  }
};

export const urlify = (text: string) => {
  if (text?.includes('<a href')) {
    return text;
  }

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, url => {
    return `<a href="${url}" target="_blank">${url}</a>`;
  });
};

export function isAppointmentActive(appt: IAppointment) {
  const status = appt.status;
  return !(
    status === 'closed' ||
    status === 'cancelled' ||
    status === 'no-show' ||
    status === 'overflow' ||
    status === 'completed'
  );
}

// basic helper function to return the _id of a database resource and checks if it's populated.
// NOTE: This util is bundled into browser apps (emr-web, patient-web, etc.), so it must not
// rely on `mongoose.isObjectIdOrHexString`, which is undefined in mongoose's browser build and
// throws "isObjectIdOrHexString is not a function" when called client-side.
export const ResourceId = (obj: any): string => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (typeof obj !== 'object') {
    return obj;
  }
  // Populated document (or DTO) carries its own `_id`.
  if (obj._id !== undefined && obj._id !== null) {
    return obj._id.toString();
  }
  // Otherwise it's an ObjectId-like instance that stringifies to its hex id.
  return obj.toString();
};

export { convertToHttpParams } from './http-params';

export const isValidUsPostalCode = (postalCode: string) => {
  return /^[0-9]{5}(?:-[0-9]{4})?$/.test(postalCode);
};

export const isValidCaPostalCode = (postalCode: string) => {
  return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(
    postalCode
  );
};

export function postalCodePatternValidator(
  selectedProvince: string,
  postalCode: string
) {
  if (selectedProvince === 'OTHER') {
    if (!isValidCaPostalCode(postalCode) && !isValidUsPostalCode(postalCode)) {
      return { pattern: true };
    }
    return null;
  }

  const isCaProvince = caProvinces.find(
    province => province.code == selectedProvince
  );
  const isUsProvince = usStates.find(state => state.code == selectedProvince);

  if (isCaProvince && !isValidCaPostalCode(postalCode)) {
    return { pattern: true };
  }

  if (isUsProvince && !isValidUsPostalCode(postalCode)) {
    return { pattern: true };
  }

  return null;
}

export const stringToBoolean = stringValue => {
  if (typeof stringValue === 'boolean') {
    return stringValue;
  }

  switch (stringValue?.toLowerCase()?.trim()) {
    case 'true':
    case 'yes':
    case '1':
      return true;

    case 'false':
    case 'no':
    case '0':
    case '':
    case null:
    case undefined:
      return false;

    default:
      return JSON.parse(stringValue);
  }
};

export const toTitleCase = (text: string) =>
  text
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1));

export const hasRole = (
  org: string,
  user: { permissions: { organization: string; role: string }[] },
  role?: UserRole | UserRole[],
  exactMatch?: boolean
): boolean => {
  const userRole = user?.permissions.find(x => x.organization === org)
    ?.role as UserRole;

  if (!userRole) return false;

  if (!role) {
    // has any role in the org
    return true;
  }
  if (exactMatch) {
    return Array.isArray(role) ? role.includes(userRole) : userRole === role;
  } else {
    return doesRoleIncludes(userRole, role);
  }
};

export const doesRoleIncludes = (
  userRole: string,
  targetRole: UserRole | UserRole[]
) => {
  const userRoles = userRole.split('-');

  if (typeof targetRole === 'string') {
    return userRoles.includes(targetRole);
  }

  if (Array.isArray(targetRole)) {
    return targetRole.some(_role => userRoles.includes(_role));
  }

  return false;
};

export const getDomainFromLocation = () => {
  if (
    window.location.hostname.includes('netlify') ||
    window.location.hostname === 'localhost'
  ) {
    return window.location.hostname;
  } else {
    // return remove subdomain so cookies works across crm and emr
    return window.location.hostname.match(
      /^(?:.*?\.)?([a-zA-Z0-9\-_]{3,}\.(?:\w{2,8}|\w{2,4}\.\w{2,4}))$/
    )[1];
  }
};

export const isAppointmentCloseToStart = date => {
  const reservation = parseISO(date as unknown as string);

  return isAfter(new Date(), subMinutes(reservation, 15));
};

export const isProvider = (role: string): boolean => {
  return role === 'provider' || role === 'provider-admin';
};

export const convertToCamelCase = (data: any) => {
  if (typeof data !== 'object') {
    return data;
  }

  const convertedData = {};
  const tempData = { ...data };
  Object.keys(tempData).map((key: string) => {
    Object.assign(convertedData, {
      [camelCase(key).replace('_', '')]:
        tempData[key] && typeof tempData[key] === 'object'
          ? convertToCamelCase(tempData[key])
          : tempData[key]
    });
  });

  return convertedData;
};

function isAllNumber(str: string) {
  return /^[0-9]+$/.test(str);
}

export function getTrackingLink(
  trackingCode: string,
  carrier?: string,
  postalCode?: string
) {
  if (!!carrier) {
    carrier = carrier.replace(/\s/g, '').toLowerCase();
  }

  //const carrier = this.getCarrier();
  if (!trackingCode) return null;
  trackingCode = trackingCode.toLowerCase();
  //if (trackingCode.startsWith('2012') && trackingCode.length === 16)
  if (carrier === 'canadapost') {
    //CANADA POST
    return `https://www.canadapost-postescanada.ca/track-reperage/en#/details/${trackingCode}`;
  } else if (carrier === 'fedex') {
    // FEDEX
    // Fed Ex ground 78 OR express/economy starts with 28
    return `https://www.fedex.com/fedextrack/?trknbr=${trackingCode}`;
  } else if (carrier === 'fleetoptics') {
    return `https://delivery.fleetopticsinc.com/tracking/?tracking_number=${trackingCode}&zip=${postalCode}`;
  } else if (carrier === 'usps') {
    return `https://tools.usps.com/go/TrackConfirmAction.action?tLabels=${trackingCode}`;
  } else if (carrier?.startsWith('ups')) {
    /// UPS   //(trackingCode.startsWith('1Z') && trackingCode.length === 18)
    return `https://www.ups.com/track?loc=en_CA&tracknum=${trackingCode}`;
  } else if (trackingCode.startsWith('logx')) {
    return `https://intelcom.ca/en/track-your-package/?tracking-id=${trackingCode}`;
  } else if (carrier === 'shippie' || carrier === '2966') {
    // Shippie tracking - shipstation returns 2966 for Shippie carrierCode
    return `https://track.shippie.ca/${trackingCode}`;
  } else if (carrier === 'purolator') {
    return `https://www.purolator.com/en/shipping/tracker?pin=${trackingCode}`;
  } else if (carrier === 'easypost') {
    return `https://www.aftership.com/track/${trackingCode}`;
  } else if (trackingCode.startsWith('2012') && trackingCode.length === 16) {
    return `https://www.canadapost-postescanada.ca/track-reperage/en#/details/${trackingCode}`;
  } else if (
    isAllNumber(trackingCode) &&
    (trackingCode.startsWith('78') ||
      trackingCode.startsWith('28') ||
      trackingCode.startsWith('39')) &&
    (trackingCode.length === 12 || trackingCode.length === 13)
  ) {
    // FEDEX
    // Fed Ex ground 78 OR express/economy starts with 28
    return `https://www.fedex.com/fedextrack/?trknbr=${trackingCode}`;
  } else {
    return `https://www.aftership.com/track/${trackingCode}`;
  }
}
/**
 *
 * @param province the full province or state name case-insensitive
 * @returns the 2 letter province or state code in uppercase
 */
export function convertProvinceToCode(province: string) {
  if (!province) return '';
  const provinces = [...usStates, ...caProvinces];
  return provinces.find(
    p => p.province.toLowerCase() === province.toLowerCase()
  )?.code;
}

export function getCountryCodeFromProvince(provinceCode: string): 'CA' | 'US' {
  if (!provinceCode) return undefined;
  return usStates.some(s => s.code === provinceCode?.toUpperCase())
    ? 'US'
    : caProvinces.some(s => s.code === provinceCode?.toUpperCase())
      ? 'CA'
      : undefined;
}

/** Prefer {@link IAddress.provinceCode}; fall back to deprecated {@link IAddress.province}. */
export function getProvinceCodeFromAddress(
  address?: Partial<IAddress> | null
): string | undefined {
  return address?.provinceCode || address?.province;
}

export function getCountryCodeFromAddress(address: IAddress): 'CA' | 'US' {
  const countryCode = address?.countryCode?.toUpperCase();

  if (countryCode === 'CA' || countryCode === 'US') {
    return countryCode;
  }
  return (
    getCountryCodeFromProvince(getProvinceCodeFromAddress(address)) ||
    (address?.countryCode?.toLowerCase() === 'canada' ? 'CA' : 'US')
  );
}

/** does not modify the original date, it return a new copy with the new time */
export function combineDateAndTime(date: Date, time: Date) {
  const _date = new Date(date);
  const hours = time.getHours();
  const minutes = time.getMinutes();
  _date.setHours(hours);
  _date.setMinutes(minutes);
  _date.setSeconds(0);
  _date.setMilliseconds(0);
  return _date;
}

/**
 * Function use to extract and minimze the medication, allergies, and medical conditions from facesheet
 * @param facesheet the facesheet consult
 * @param type the answers to be extracted
 */
export function extractFromFacesheet(
  facesheet,
  field: 'medications' | 'allergies' | 'conditions'
) {
  if (field === 'medications') {
    return (
      [
        ...(facesheet?.answers?.medications?.list?.map(_ => _.name) || []),
        ...(facesheet?.answers?.supplements?.list?.map(_ => _.name) || [])
      ].join('; ') || 'None'
    );
  } else {
    //allergies || conditions
    // A facesheet that simply has no answers for this section means "none
    // selected", same as the `medications` branch above. Without the `?? {}`
    // this threw a raw `TypeError` from `Object.keys` (CAR-2157). Callers that
    // require a facesheet must check for one themselves — 'None' here is an
    // answer, not a stand-in for a missing document.
    const answerKeys = Object.keys(facesheet?.answers?.[field] ?? {});
    const qc = QuestionsCatalog.find(qc => qc.field === field);
    let answerString = qc.answer.options
      .filter(a => a.key != 'other' && answerKeys.includes(a.key))
      .map(o => o.value)
      .join('; ');

    if (answerKeys.includes('otherDesc')) {
      answerString =
        answerString +
        (answerString ? '; ' : '') +
        facesheet?.answers?.[field]?.otherDesc;
    }

    return answerString || 'None';
  }
}

export function extractFromDrugCard(drugCardConsult: IConsultation) {
  const insurances: Array<{
    type:
    | 'PRIMARY'
    | 'SECONDARY'
    | 'PROVINCIAL'
    | 'SECONDARY2'
    | 'SECONDARY3'
    | 'GOVERNMENT_ID';
    insuranceProvider?: string;
    cardNum?: string;
    images: Array<{
      isFront: boolean;
      insuranceDocument: {
        documentEncodedString: string;
        documentName: string;
      };
    }>;
  }> = [];

  if (!drugCardConsult?.answers) {
    return insurances;
  }

  const answers = drugCardConsult.answers as any;

  // Extract all drug cards (supports both formats)
  const drugCards = extractDrugCardsFromAnswers(answers);

  // Convert to insurance format
  drugCards.forEach((card, index) => {
    const insuranceProvider = card.plan
      .split('-')
      .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');

    const cardNum = card.info?.certificateNum
      ? String(card.info.certificateNum)
      : undefined;

    let type: 'PRIMARY' | 'SECONDARY' | 'SECONDARY2' | 'SECONDARY3' = 'PRIMARY';
    if (index === 1) type = 'SECONDARY';
    else if (index === 2) type = 'SECONDARY2';
    else if (index >= 3) type = 'SECONDARY3';

    insurances.push({
      type,
      insuranceProvider: insuranceProvider || card.plan,
      cardNum,
      images: [] // No images as per user requirement
    });
  });

  return insurances;
}

/**
 * Extract drug cards from answers (legacy format - numbered fields)
 */
export function extractDrugCardsFromAnswers(answers: any): Array<{
  plan: string;
  planName: string;
  info: any;
  owner: any;
  name: any;
}> {
  const drugCards: Array<{
    plan: string;
    planName: string;
    info: any;
    owner: any;
    name: any;
  }> = [];

  if (!answers) {
    return drugCards;
  }

  // Helper to get plan display name
  const getPlanName = (plan: string): string => {
    const planMap: { [key: string]: string } = {
      'CS': 'Claimsecure',
      'sunlife-ESI': 'SunLife',
      'Manulife-ESI': 'Manulife',
      'OTIP-ESI': 'OTIP',
      'Empire-ESI': 'Empire Life',
      'IA-ESI': 'Industrial Alliance (IA)',
      'Johnson-ESI': 'Johnson Inc',
      'RWAM-ESI': 'RWAM',
      'Canadalife-AHE': 'Canada Life',
      'GWL-AHE': 'Great West Life (GWL)',
      'Des-AHE': 'Desjardins',
      'Equitable-AHE': 'Equitable Life',
      'PSHCP-AHE': 'Public Service Health Care Plan',
      'Johnston-AHE': 'Johnston Group',
      'Ontario-OBC': 'Ontario Blue Cross',
      'Medvie-OBC': 'Medavie Blue Cross',
      'ABBC': 'Alberta Blue Cross',
      'MBC': 'Manitoba Blue Cross',
      'SBC': 'Saskatchewan Blue Cross',
      'GSC': 'Greenshield',
      'RBC-GSC': 'RBC Insurance',
      'PBC': 'Pacific Blue Cross',
      'MDM': 'MDM Guard',
      'NX': 'NexgenRx',
      'CBP': 'Canadian Benefit Providers',
      'RCMP': 'RCMP',
      'SSQ': 'SSQ',
      'Discount-STI-ESI': 'Discount Card ESI',
      'Discount-NX': 'Discount Card NX'
    };
    return planMap[plan] || plan;
  };

  // Extract from numbered fields (legacy format)
  // Extract first card
  if (answers.drugCardExists?.yes && answers.drugCardPlan && answers.drugCardInfo) {
    const plan = answers.drugCardPlan.plan;
    drugCards.push({
      plan: plan,
      planName: getPlanName(plan),
      info: answers.drugCardInfo,
      owner: answers.drugCardOwner,
      name: answers.drugCardName
    });
  }

  // Extract additional cards (supports up to 5 cards)
  let cardIndex = 2;
  let hasMoreCards = answers.drugCardAnother?.yes;

  while (hasMoreCards && cardIndex <= 5) {
    const planKey = `drugCardPlan${cardIndex}`;
    const infoKey = `drugCardInfo${cardIndex}`;
    const ownerKey = `drugCardOwner${cardIndex}`;
    const nameKey = `drugCardName${cardIndex}`;
    const anotherKey = `drugCardAnother${cardIndex === 2 ? '' : cardIndex - 1}`;

    const plan = answers[planKey]?.plan;
    const cardInfo = answers[infoKey];

    if (plan && cardInfo) {
      drugCards.push({
        plan: plan,
        planName: getPlanName(plan),
        info: cardInfo,
        owner: answers[ownerKey],
        name: answers[nameKey]
      });
    }

    hasMoreCards = answers[anotherKey]?.yes;
    cardIndex++;
  }

  return drugCards;
}

export function removeNullOrUndefinedFromObject(obj: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, v]) => v !== null && v !== undefined)
      .map(([k, v]) => [
        k,
        v === Object(v) ? removeNullOrUndefinedFromObject(v) : v
      ])
  );
}

export function remove__xxx__FromProductName(name: string) {
  const l = name.indexOf('__');
  const r = name.lastIndexOf('__');

  if (l !== -1 && r !== l) {
    const segmentToRemove = '__' + name.slice(l + 2, r) + '__';
    const _name = name.replace(segmentToRemove, '');
    return _name;
  }

  return name;
}

export function getLicenseForProvince(
  licenses: Array<ILicense> = [],
  province?: string
): ILicense | undefined {
  const provinceAbbreviations: Record<string, string> = {
    ALBERTA: 'AB',
    BRITISH_COLUMBIA: 'BC',
    MANITOBA: 'MB',
    NEW_BRUNSWICK: 'NB',
    NEWFOUNDLAND_AND_LABRADOR: 'NL',
    NOVA_SCOTIA: 'NS',
    ONTARIO: 'ON',
    PRINCE_EDWARD_ISLAND: 'PE',
    QUEBEC: 'QC',
    SASKATCHEWAN: 'SK',
    NORTHWEST_TERRITORIES: 'NT',
    NUNAVUT: 'NU',
    YUKON: 'YT'
  };
  const provinceKey = province?.trim?.().toUpperCase?.().replace(/\s+/g, '_');
  const normalizedProvince =
    provinceAbbreviations[provinceKey || ''] || provinceKey;
  return (
    licenses.find(
      license => license?.jurisdiction?.toUpperCase?.() === normalizedProvince
    ) ||
    licenses.find(license => license?.jurisdiction?.toUpperCase?.() === 'ON') ||
    licenses[0]
  );
}

export function getLicenseJurisdictionForTemplate(templateId?: string): string {
  const normalizedTemplateId = templateId?.trim?.().toLowerCase?.();

  switch (normalizedTemplateId) {
    case 'on':
    case 'wlon':
    case 'trt1on':
      return 'ON';
    default:
      return normalizedTemplateId?.toUpperCase?.() || 'ON';
  }
}

/**
 * Returns the canonical lab requisition fax number for the given organization.
 * Format matches what is used in lab req forms (e.g. '1-855-952-2436').
 */
export function getLabReqFax(organization?: string): string {
  switch (organization) {
    case 'jill_health':
      return '1-855-477-4220';
    default:
      return '1-855-952-2436';
  }
}

export function getDateFormat(date) {
  return isThisYear(new Date(date)) ? 'MMM d @ h:mm a' : 'MMM d, yyyy @ h:mm a';
}

export function calculateNextCycleDate(
  interval,
  unit: SubscriptionCycleUnit,
  currentDate?: Date
) {
  if (unit === 'month') {
    return addMonths(currentDate || new Date(), interval);
  } else if (unit === 'week') {
    return addWeeks(currentDate || new Date(), interval);
  } else if (unit === 'day') {
    return addDays(currentDate || new Date(), interval);
  } else if (unit === 'year') {
    return addYears(currentDate || new Date(), interval);
  }
  throw new Error('Invalid unit');
}

/**
 * Returns the number of days in one subscription filling cycle.
 */
export function getCycleDaysInDays(
  interval: number,
  unit: SubscriptionCycleUnit,
  fromDate?: Date
): number {
  const base = fromDate || new Date();
  const nextDate = calculateNextCycleDate(interval, unit, base);
  return differenceInDays(nextDate, base);
}

/**
 * Returns the number of days in the subscription's current cycle based on its phase config.
 */
export function getSubscriptionCurrentCycleLengthInDays(subscription: {
  phases: IProductSubscriptionPhase[];
  currentCycle: number;
  nextCycleDate?: Date;
}): number {
  const phase = getPhaseByCycle(subscription.phases, subscription.currentCycle);
  if (!phase) {
    throw new Error('No phase found for current cycle');
  }

  const interval = Number(phase.fillingCycleInterval) || 1;
  const unit = phase.fillingCycleUnit || 'day';

  return getCycleDaysInDays(interval, unit);
}

/**
 * @param phases the phases of the product
 * @param cycle the current cycle of the subscription
 * @returns the next phase or null if no more phases
 */
export function getPhaseByCycle(
  phases: IProductSubscriptionPhase[],
  cycle: number // start from first phase = 1
): IProductSubscriptionPhase | null {
  const index = getPhaseIndexByCycle(phases, cycle);
  return index !== null ? phases[index] : null;
}

export function getPhaseIndexByCycle(
  phases: IProductSubscriptionPhase[],
  cycle: number // start from first phase = 1
): number | null {
  let selectedPhaseIndex: number | null = null;
  let cycleCursor = cycle;
  for (let i = 0; i < phases?.length; i++) {
    const p = phases[i];
    const phaseCycles = Number(p.cycles);
    cycleCursor -= phaseCycles;
    if (phaseCycles === 0 || cycleCursor <= 0) {
      // phaseCycles === 0 means the phase is infinite
      selectedPhaseIndex = i;
      break;
    }
  }
  return selectedPhaseIndex;
}

export function isCycleBillable(
  phases: IProductSubscriptionPhase[],
  targetCycle: number // base-1 index (ie. 1st cycle is 1, 2nd cycle is 2, etc.)
) {
  const currentPhaseIndex = getPhaseIndexByCycle(phases, targetCycle);
  if (currentPhaseIndex === null) return null;
  const currentPhase = phases[currentPhaseIndex];
  const cyclesBeforeCurrentPhase = phases
    .slice(0, currentPhaseIndex)
    .reduce((acc, p, i) => {
      return acc + (Number(p.cycles) || 1);
    }, 0);

  const targetCycleInCurrentPhase = targetCycle - cyclesBeforeCurrentPhase - 1;
  return (
    targetCycleInCurrentPhase % (currentPhase.billableCycleInterval || 1) === 0
  );
}

export function isCycleFulfillment(
  phases: IProductSubscriptionPhase[],
  targetCycle: number
): boolean | null {
  const currentPhaseIndex = getPhaseIndexByCycle(phases, targetCycle);
  if (currentPhaseIndex === null) return null;
  const currentPhase = phases[currentPhaseIndex];
  const interval = currentPhase.fulfillmentCycleInterval || 1;
  const cyclesBeforeCurrentPhase = phases
    .slice(0, currentPhaseIndex)
    .reduce((acc, p) => acc + (Number(p.cycles) || 1), 0);
  const targetCycleInCurrentPhase = targetCycle - cyclesBeforeCurrentPhase; // 1-based in phase
  return (targetCycleInCurrentPhase - 1) % interval === 0;
}

export function doesCycleRequireNewRequirement(
  phases: IProductSubscriptionPhase[],
  targetCycle: number
): boolean | null {
  const currentPhaseIndex = getPhaseIndexByCycle(phases, targetCycle);
  if (currentPhaseIndex === null) return null;
  const currentPhase = phases[currentPhaseIndex];
  const interval = currentPhase?.requirementCycleInterval || 1; // fallback to 1 for backward compatibility
  const cyclesBeforeCurrentPhase = phases
    .slice(0, currentPhaseIndex)
    .reduce((acc, p) => acc + (Number(p.cycles) || 1), 0);
  const targetCycleInCurrentPhase = targetCycle - cyclesBeforeCurrentPhase; // 1-based in phase
  return (targetCycleInCurrentPhase - 1) % interval === 0;
}

export function isValidCVV(cvv: string): boolean {
  return !!cvv && /^[0-9]{3,4}$/.test(cvv);
}

export const retry = (callback: () => void, intervalMs = 1000, times = 3) => {
  let numberOfTries = 0;
  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      numberOfTries++;
      if (numberOfTries === times) {
        clearInterval(interval);
        reject(new Error('Failed to execute callback'));
      }
      try {
        await callback();
        clearInterval(interval);
        resolve(void 0);
      } catch (err) {
        // do nothing, next try will happen
      }
    }, intervalMs);
  });
};

export function normalizeAddress(address: IAddress): string {
  const abbreviations: Record<string, string> = {
    ave: 'avenue',
    st: 'street',
    rd: 'road',
    blvd: 'boulevard',
    ln: 'lane',
    dr: 'drive',
    hwy: 'highway',
    n: 'north',
    s: 'south',
    e: 'east',
    w: 'west'
  };

  function normalizeComponent(component: string | null | undefined): string {
    if (!component) return '';
    return component
      .toLowerCase()
      .replace(/[.,]/g, '') // remove punctuation
      .split(' ')
      .map(word => abbreviations[word] || word) // expand abbreviations
      .join(' ')
      .trim();
  }

  const {
    address1 = '',
    address2 = '',
    city = '',
    provinceCode = '',
    postalCode = '',
    countryCode = ''
  } = address;

  const parts = [
    normalizeComponent(address1),
    normalizeComponent(address2),
    normalizeComponent(city),
    normalizeComponent(provinceCode),
    normalizeComponent(postalCode),
    normalizeComponent(countryCode)
  ];

  return parts.filter(Boolean).join(', ');
}

export function calculateOrderFields(order: IOrder): IOrder & {
  rxResults?: {
    prescriptions: Prescription[];
    type: OrderPrescriptionType;
  };
} {
  if (!order) return order;
  // Discount Amount
  let calculatedDiscountAmount = 0;
  if (order?.source === 'manual') {
    calculatedDiscountAmount = order?.discountAmount || 0;
  } else if (order?.subTotalAmount != null && order?.totalAmount != null) {
    calculatedDiscountAmount =
      order.subTotalAmount -
      (order.totalAmount +
        (order.creditUsedAmount || 0) +
        (order.totalInsuranceAmount || 0));
  }
  calculatedDiscountAmount =
    calculatedDiscountAmount > 0 ? calculatedDiscountAmount : 0;

  // Subtotal Amount
  let calculatedSubtotalAmount = 0;
  if (order?.source === 'manual') {
    calculatedSubtotalAmount = order?.baseAmount ?? 0;
  } else {
    calculatedSubtotalAmount = order?.subTotalAmount ?? 0;
  }

  // Insurance Amount
  let calculatedInsuranceAmount = 0;
  calculatedInsuranceAmount =
    order?.totalInsuranceAmount ||
    order?.lineItems?.reduce((acc, item) => {
      return acc + (item.totalInsuranceAmount || 0);
    }, 0);

  return {
    ...order,
    discountAmount: calculatedDiscountAmount,
    subTotalAmount: calculatedSubtotalAmount,
    totalInsuranceAmount: calculatedInsuranceAmount
  };
}

export function downloadCSV(csvString: string, filename = 'orders.csv') {
  const blob = new Blob([csvString], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Max decoded PDF size for server-side upload validation (25 MiB). */
const MAX_PDF_BYTES = 25 * 1024 * 1024;

/** Base64 PDF upload: MIME type, size cap, and `%PDF` magic (lab results inbox, etc.). */
export function assertValidBase64PdfPayload(
  file: { buffer: string; mimetype: string },
  maxBytes: number = MAX_PDF_BYTES
): void {
  if (!file?.buffer) {
    throw new Error('File data is required');
  }
  const stripped = file.buffer.replace(/^data:[^;]+;base64,/, '');
  if (!stripped.length) {
    throw new Error('File data is required');
  }
  const L = stripped.length;
  let padding = 0;
  if (stripped[L - 1] === '=') padding++;
  if (L >= 2 && stripped[L - 2] === '=') padding++;
  const estimatedDecodedBytes = Math.floor((L * 3) / 4) - padding;
  if (estimatedDecodedBytes > maxBytes) {
    throw new Error(`PDF exceeds maximum size of ${maxBytes} bytes`);
  }
  const decoded = Buffer.from(stripped, 'base64');
  if (decoded.length === 0) {
    throw new Error('File data could not be decoded');
  }
  if (decoded.length > maxBytes) {
    throw new Error(`PDF exceeds maximum size of ${maxBytes} bytes`);
  }
  if (file.mimetype !== 'application/pdf') {
    throw new Error('MIME type must be application/pdf');
  }
  const pdfHeader = Buffer.from('%PDF', 'ascii');
  if (
    decoded.length < pdfHeader.length ||
    !decoded.subarray(0, pdfHeader.length).equals(pdfHeader)
  ) {
    throw new Error('File content is not a valid PDF (expected %PDF header)');
  }
}
export const validateNextCycleDate = (
  nextCycleDateParam: string | Date | undefined,
  isRequired = true
): { error?: string } | undefined => {
  if (!nextCycleDateParam && !isRequired) return undefined;
  if (!nextCycleDateParam) return { error: 'Next cycle date is required' };

  const nextCycleDate =
    nextCycleDateParam instanceof Date
      ? nextCycleDateParam
      : new Date(nextCycleDateParam);

  if (Number.isNaN(nextCycleDate.getTime())) {
    return { error: 'Next cycle date is not a valid date' };
  }
  const today = startOfDay(new Date());
  const nextCycleDateAtStartOfDay = startOfDay(nextCycleDate);
  const minDate = addDays(today, 1);
  const maxDate = addYears(today, 1);

  if (nextCycleDateAtStartOfDay < minDate) {
    return { error: 'Next cycle date must be a future date' };
  }
  if (nextCycleDateAtStartOfDay > maxDate) {
    return { error: 'Next cycle date cannot be more than 1 year from now' };
  }
};

export const validateCurrentCycle = (
  phases: IProductSubscriptionPhase[],
  currentCycle: number
): { error?: string } | undefined => {
  if (!phases || phases.length === 0) {
    return { error: 'Product must have at least one subscription phase' };
  }

  if (currentCycle < 1) {
    return { error: 'Current cycle must be greater than 0' };
  }

  const isLastPhaseZeroCycle = phases[phases.length - 1].cycles === 0;
  if (isLastPhaseZeroCycle) {
    return undefined;
  }

  const sumOfPhases = phases.reduce((acc, phase) => {
    const cycles = Number(phase.cycles);
    return acc + (Number.isFinite(cycles) ? cycles : 1);
  }, 0);
  if (currentCycle > sumOfPhases) {
    return {
      error:
        'Current cycle can not be more than sum of all subscription phase cycles'
    };
  }

  if (currentCycle === sumOfPhases) {
    return {
      error: 'Subscription has reached the maximum cycle limit'
    };
  }

  return undefined;
};

export const normalizeCurrency = (
  value?: string
): 'USD' | 'CAD' | undefined => {
  const normalized = value?.toUpperCase();
  return normalized === 'USD' || normalized === 'CAD'
    ? normalized
    : undefined;
};

export function sanitizePhone(phone?: string): string | undefined {
  if (!phone) return undefined;
  let normalized = phone.replace(/^[0+]|[\D]/g, '').trim();
  if (normalized.length > 10) {
    normalized = normalized.substring(normalized.length - 10);
  }
  return `+1${normalized}`;
}

export function buildPhoneLookupCandidates(rawPhone?: string): string[] {
  const raw = rawPhone?.trim();
  if (!raw) {
    return [];
  }

  const digitsOnly = raw.replace(/\D/g, '');
  const local10 =
    digitsOnly.length === 11 && digitsOnly.startsWith('1')
      ? digitsOnly.slice(1)
      : digitsOnly;
  const e164 =
    local10.length === 10
      ? `+1${local10}`
      : raw.startsWith('+')
        ? raw
        : `+${digitsOnly}`;

  return Array.from(new Set([raw, digitsOnly, local10, e164].filter(v => !!v)));
}

export function buildHealthCardLookupCandidates(rawHealthCard?: string): string[] {
  const raw = rawHealthCard?.trim();
  if (!raw) {
    return [];
  }

  const upperRaw = raw.toUpperCase();
  const compact = upperRaw.replace(/[\s-]/g, '');
  const alphanumeric = upperRaw.replace(/[^A-Z0-9]/g, '');
  const digitsOnly = upperRaw.replace(/\D/g, '');
  const candidates = [raw, upperRaw, compact, alphanumeric, digitsOnly].filter(
    value => !!value && value.length > 0
  );
  return Array.from(new Set(candidates));
}

export function buildHealthCardDigitsSuffixRegex(
  digitsOnly: string,
  minDigits = 8
): RegExp | null {
  if (!digitsOnly || digitsOnly.length < minDigits) {
    return null;
  }
  return new RegExp(`^${digitsOnly}[A-Z]{0,3}$`);
}

/**
 * Format-insensitive health-card matcher for DB lookup.
 * Allows optional spaces/dashes between digits and optional trailing version
 * letters (e.g. OHIP), so Llama's `12346820` matches CRM's `1234-6820` / `1234 6820 AB`.
 */
export function buildHealthCardFlexibleMatchRegex(
  digitsOnly: string,
  minDigits = 8
): RegExp | null {
  if (!digitsOnly || digitsOnly.length < minDigits || !/^\d+$/.test(digitsOnly)) {
    return null;
  }
  const withOptionalSeparators = digitsOnly.split('').join('[\\s\\-]*');
  return new RegExp(
    `^[\\s\\-]*${withOptionalSeparators}[\\s\\-]*[A-Z]{0,3}$`,
    'i'
  );
}
export type GetAvailableRequirementsOptions = {
  /**
   * When false, excludes `placeholder` requirements (they are order-only cues,
   * not questionnaires). Defaults to true so product/order requirement pickers
   * still see them.
   */
  includePlaceholders?: boolean;
};

export function getAvailableRequirements(
  selectedLayout?: IEMRLayout | null,
  options?: GetAvailableRequirementsOptions
) {
  if (!selectedLayout) {
    return [];
  }

  const includePlaceholders = options?.includePlaceholders !== false;

  const questionnaires = (
    selectedLayout.questionnaires || []
  ).map(qKey => ({
    key: qKey,
    type: 'qnr',
    name: formTypes[qKey]?.name
  }));

  const verificationRequirements = (
    selectedLayout.verificationRequirements || []
  ).map(req => ({
    key: req.key,
    type: 'verification',
    name: req.name
  }));

  const availableRequirements = (
    selectedLayout.availableRequirements || []
  ).reduce((acc, requirement) => {
    if (requirement.type === 'lab_test') {
      acc.push({
        key: requirement.id,
        type: 'lab_test',
        name: requirement.name,
        extras: requirement.extras
      });
    } else if (requirement.type === 'qnr') {
      acc.push({
        key: requirement.id,
        type: 'qnr',
        name: requirement.name || formTypes[requirement.id]?.name,
        extras: requirement.extras
      });
    } else if (requirement.type === 'verification') {
      acc.push({
        key: requirement.id,
        type: 'verification',
        name: requirement.name,
        extras: requirement.extras
      });
    } else if (
      requirement.type === 'placeholder' &&
      includePlaceholders
    ) {
      acc.push({
        key: requirement.id,
        type: 'placeholder',
        name: requirement.name,
        extras: requirement.extras
      });
    }

    return acc;
  }, []);

  return [
    ...questionnaires,
    ...verificationRequirements,
    ...availableRequirements
  ];
}

/**
 * Normalize an email for storage/lookup and before sending it to external
 * providers. Lowercases, removes whitespace, and strips stray leading/trailing
 * dots (e.g. "Foo@Bar.com." -> "foo@bar.com"), which providers such as Stripe
 * reject as invalid. Does not touch the `uname` login key.
 */
export function normalizeEmail(email: string | null | undefined): string {
  if (!email) return '';
  return email
    .replace(/\s+/g, '')
    .toLowerCase()
    .replace(/^\.+|\.+$/g, '');
}

/**
 * Backstop shape check used before calling providers that reject malformed
 * addresses. Intentionally permissive: local@domain.tld, no whitespace, and a
 * dotted domain. Pair with normalizeEmail (normalize first, then validate).
 */
export function isValidEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
